import dayjs from "dayjs";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  applyIndependentRideDateChange,
  editFormDataAF,
  Log,
  RideCrudModeKeyTuple,
  RideFormFields,
  rideManualDateChangeInfoAtom,
  rideStartEndChangeHandler,
  RideStationPanelMode,
  trimSeconds,
  useFormats,
  useSystemParams,
  useTimezone,
  type IRideDto,
} from "@ryde/shared";

import { useRideDtoActions } from "./use-ride-dto-actions";
import { useRideEmptyStationsAllowed } from "./use-ride-empty-stations-allowed";

export const useRideCrudMethods = () => {
  const { timezone } = useTimezone();
  const { formatDateTimeNormalized, timeFormat } = useFormats();
  const { allowShiftTimeOverrideDuringManualAction } = useSystemParams();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );

  const setRideManualDateChangeInfo = useSetRecoilState(
    rideManualDateChangeInfoAtom,
  );
  const {
    updateEditingRideMetadataRideDate,
    updateEditingRideDate,
    updateEditingRideSingleDate,
  } = useRideDtoActions();

  const { isRideHasIndependentDayAdjustment } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDto,
  );

  const onRideStartBlurEvent = useEvent(
    async (updatedRideStartValueRaw: TDateISO) => {
      Log.debug(
        "Ride on blur start date - ride start blur value:",
        formatDateTimeNormalized(updatedRideStartValueRaw),
        "dto value:",
        formatDateTimeNormalized(rideDto?.rideStart),
      );
      const updatedRideStartValue = trimSeconds(
        updatedRideStartValueRaw,
      ) as TDateISO;

      if (isRideHasIndependentDayAdjustment) {
        const independentDateChange = applyIndependentRideDateChange({
          rideDto: rideDto || {},
          updatedRideDateTime: updatedRideStartValue,
          rideStartEndKey: "rideStart",
        });

        updateEditingRideSingleDate({
          rideId: rideDto?.id,
          dateValue: updatedRideStartValue,
          fieldToUpdate: "rideStart",
          metadata: independentDateChange.metadata,
          extraDates: independentDateChange.rideEnd
            ? { rideEnd: independentDateChange.rideEnd }
            : undefined,
        });
        return;
      }

      if (!rideDto?.metadata?.stationIds?.length) {
        updateEditingRideDate(
          rideDto?.id || 0,
          updatedRideStartValue ?? rideDto?.rideStart ?? dayjs().toISOString(),
          updatedRideStartValue,
        );

        return;
      }

      setRideManualDateChangeInfo({
        fieldChanged: RideFormFields.rideStart,
        value: updatedRideStartValue,
      });

      const result = rideStartEndChangeHandler({
        rideDto,
        updatedRideDateTime: updatedRideStartValue,
        rideStartEndKey: "rideStart",
        timeFormat,
        timezone,
        allowShiftTimeOverrideDuringManualAction,
      });

      if (!result || result === "revert") {
        Log.debug(
          "Ride start value is not acceptable, changes rejected",
          result,
        );
        return;
      }

      updateEditingRideMetadataRideDate({
        rideId: rideDto?.id || 0,
        updatedMetadata: result.metadataUpdated,
      });

      Log.debug(
        "Ride start value is valid, updated metadata",
        result.metadataUpdated,
      );
    },
  );

  const onRideEndBlurEvent = useEvent(
    async (updatedRideEndValueRaw: TDateISO) => {
      const updatedRideEndValue = trimSeconds(
        updatedRideEndValueRaw,
      ) as TDateISO;
      Log.debug(
        "Ride on blur end date - ride end blur value:",
        formatDateTimeNormalized(updatedRideEndValueRaw),
        "dto value:",
        formatDateTimeNormalized(rideDto?.rideEnd),
      );

      if (isRideHasIndependentDayAdjustment) {
        const independentDateChange = applyIndependentRideDateChange({
          rideDto: rideDto || {},
          updatedRideDateTime: updatedRideEndValue,
          rideStartEndKey: "rideEnd",
        });

        updateEditingRideSingleDate({
          rideId: rideDto?.id,
          dateValue: updatedRideEndValue,
          fieldToUpdate: "rideEnd",
          metadata: independentDateChange.metadata,
          extraDates: independentDateChange.rideStart
            ? { rideStart: independentDateChange.rideStart }
            : undefined,
        });
        return;
      }

      if (!rideDto?.metadata?.stationIds?.length) {
        updateEditingRideDate(
          rideDto?.id || 0,
          updatedRideEndValue,
          updatedRideEndValue ?? rideDto?.rideEnd ?? dayjs().toISOString(),
        );

        return;
      }

      setRideManualDateChangeInfo({
        fieldChanged: RideFormFields.rideEnd,
        value: updatedRideEndValue,
      });

      const result = rideStartEndChangeHandler({
        rideDto,
        updatedRideDateTime: updatedRideEndValue,
        rideStartEndKey: "rideEnd",
        timeFormat,
        timezone,
        allowShiftTimeOverrideDuringManualAction,
      });

      if (!result || result === "revert") {
        Log.debug("Ride end value is not acceptable, changes rejected", result);
        return;
      }

      updateEditingRideMetadataRideDate({
        rideId: rideDto?.id || 0,
        updatedMetadata: result.metadataUpdated,
      });

      Log.debug(
        "Ride end value is valid, updated metadata",
        result.metadataUpdated,
      );
    },
  );
  return { onRideStartBlurEvent, onRideEndBlurEvent };
};
